import React, { memo } from "react";
import ReactApexChart from "react-apexcharts";
import { Skeleton } from "@mui/material";
import { generateUniqueColors } from "../../../../utils/chartColors";

const DonutChart = ({
  chartValues,
  loadingState,
  chartDataSet,
  customCardClass,
  onCardClick,
  inline,
  valueFormatter,
}) => {
  const seriesColors =
    chartDataSet?.barColors?.length > 0
      ? chartDataSet.barColors
      : generateUniqueColors(chartValues?.length || 1);

  const total = (chartValues || []).reduce((s, v) => s + (Number(v) || 0), 0);
  const formatValue =
    typeof valueFormatter === "function" ? valueFormatter : (value) => value;

  const donutSize = 180;
  const donutOptions = {
    chart: {
      type: "donut",
      sparkline: { enabled: true },
      events: {
        dataPointSelection: (_e, _ctx, config) => {
          if (!chartDataSet?.onSegmentClick) return;
          setTimeout(() => chartDataSet.onSegmentClick?.(config?.dataPointIndex), 0);
        },
      },
    },
    states: { hover: { filter: { type: "none" } } },
    dataLabels: { enabled: false },
    labels: chartDataSet?.chartLabels || [],
    plotOptions: {
      pie: {
        startAngle: 0,
        endAngle: 360,
        expandOnClick: false,
        donut: {
          size: "55",
          labels: { show: false },
        },
      },
    },
    colors: seriesColors,
    fill: { type: "solid" },
    legend: { show: false },
    tooltip: { enabled: true, style: { fontSize: "12px" } },
  };

  const indicatorRows = (chartDataSet?.chartIndicators?.length
    ? chartDataSet.chartIndicators.map((e, i) => ({
      label: e?.value,
      value: (chartValues || [])[i] ?? 0,
      color: e?.color || seriesColors[i],
      onClick: e?.onClick,
    }))
    : (chartDataSet?.progressLines || []).map((e, i) => ({
      label: e?.label,
      value: e?.value ?? 0,
      color: e?.color || seriesColors[i],
      onClick: e?.onClick,
    }))
  );

  const indicatorPlacement = chartDataSet?.indicatorPlacement || "side";
  const indicatorColumns = Number(chartDataSet?.indicatorColumns || 2);
  const IndicatorRow = ({ item, index }) => (
    <div
      key={`${item.label}-${index}`}
      style={{ display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", columnGap: 10, cursor: item?.onClick ? "pointer" : "default" }}
      onClick={(ev) => { if (!item?.onClick) return; ev.stopPropagation(); item.onClick(); }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: item?.color || seriesColors[index], flexShrink: 0, display: "inline-block" }} />
        <span style={{ fontSize: "13px", fontFamily: '"gilroyMedium", Inter, sans-serif', color: "#0f172a", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {item.label}
        </span>
      </div>
      <span style={{ fontSize: "13px", fontFamily: '"gilroyMedium", Inter, sans-serif', color: "#0f172a", fontWeight: 600, textAlign: "right" }}>
        {formatValue(item.value ?? 0)}
      </span>
    </div>
  );

  const Inner = () => (
  <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
    {chartDataSet?.cardHeading && (
      <div style={{ fontSize: "14px", fontFamily: '"gilroyMedium", Inter, sans-serif', color: "#111827" }}>
        {chartDataSet.cardHeading}
      </div>
    )}

    {indicatorPlacement === "below" ? (
      <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
        <div style={{ position: "relative", width: donutSize, height: donutSize, alignSelf: "center", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClickCapture={(ev) => { if (chartDataSet?.onSegmentClick) ev.stopPropagation(); }}>
          <ReactApexChart
            options={donutOptions}
            series={chartValues || []}
            type="donut"
            width={donutSize}
            height={donutSize}
          />
          <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center", pointerEvents: "none" }}>
            {chartDataSet?.chartText ? (
              <div style={{ fontSize: "12px", fontFamily: '"gilroyBold", Inter, sans-serif', color: "#111827", lineHeight: 1.3 }}>
                {chartDataSet.chartText}
              </div>
            ) : (
              <>
                <div style={{ fontSize: "18px", fontFamily: '"gilroyBold", Inter, sans-serif', color: "#111827", lineHeight: 1 }}>{formatValue(total)}</div>
                <div style={{ fontSize: "10px", color: "#9ca3af", fontFamily: '"gilroyMedium", Inter, sans-serif' }}>Total</div>
              </>
            )}
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.max(1, indicatorColumns)}, minmax(0, 1fr))`, columnGap:16, rowGap:8  }}>
          {indicatorRows.map((e, i) => (
            <IndicatorRow key={`${e.label}-${i}`} item={e} index={i} />
          ))}
        </div>
      </div>
    ) : (
      <div style={{ display: "grid", gridTemplateColumns: "190px 1fr", gap: "1rem", alignItems: "center" }}>
        {/* Left: full 360 deg donut */}
        <div style={{ position: "relative", width: donutSize, height: donutSize, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClickCapture={(ev) => { if (chartDataSet?.onSegmentClick) ev.stopPropagation(); }}>
          <ReactApexChart
            options={donutOptions}
            series={chartValues || []}
            type="donut"
            width={donutSize}
            height={donutSize}
          />
          {/* Center label */}
          <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center", pointerEvents: "none" }}>
            {chartDataSet?.chartText ? (
              <div style={{ fontSize: "12px", fontFamily: '"gilroyBold", Inter, sans-serif', color: "#111827", lineHeight: 1.3 }}>
                {chartDataSet.chartText}
              </div>
            ) : (
              <>
                <div style={{ fontSize: "18px", fontFamily: '"gilroyBold", Inter, sans-serif', color: "#111827", lineHeight: 1 }}>{formatValue(total)}</div>
                <div style={{ fontSize: "10px", color: "#9ca3af", fontFamily: '"gilroyMedium", Inter, sans-serif' }}>Total</div>
              </>
            )}
          </div>
        </div>

        {/* Right: indicators with values */}
        <div style={{ display: "flex", flexDirection: "column", gap: "0.45", maxWidth: "200px" }}>
          {indicatorRows.map((e, i) => (
            <IndicatorRow key={`${e.label}-${i}`} item={e} index={i} />
          ))}
        </div>
      </div>
    )}
  </div>
);
  const SkeletonView = () => (
    <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
      <div style={{ flex: 1 }}>
        <Skeleton width="110px" height="20px" style={{ marginBottom: 10 }} />
        <Skeleton width="80%" height="14px" style={{ marginBottom: 6 }} />
        <Skeleton width="90%" height="14px" style={{ marginBottom: 6 }} />
        <Skeleton width="70%" height="14px" />
      </div>
      <Skeleton variant="circular" width={130} height={130} />
    </div>
  );

  if (inline) {
    return (
      <div onClick={onCardClick} style={{ cursor: onCardClick ? "pointer" : "inherit", width: "100%" }}>
        {loadingState ? <SkeletonView /> : <Inner />}
      </div>
    );
  }

  return (
    <div className={customCardClass} onClick={onCardClick}
      style={{ background: "#fff", borderRadius: "16px", border: "none", boxShadow: "0 2px 12px rgba(15,23,42,0.07)", padding: "1.25rem 1.5rem", cursor: onCardClick ? "pointer" : "default", width: "100%", boxSizing: "border-box" }}>
      {loadingState ? <SkeletonView /> : <Inner />}
    </div>
  );
};

export default memo(DonutChart);
